import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Mbp3RegisterApi } from "../../../api/Mbp3RegisterApi";


// ── MBP-3 Entry columns ───────────────────────────────────────────────────────
const ENTRY_COLUMNS = [
  { key: "serialNo",                  label: "S.No",                          col: "(1)"  },
  { key: "boardResolutionDate",       label: "Board Resolution Date",         col: "(2)"  },
  { key: "investmentDate",            label: "Date of Investment",            col: "(3)"  },
  { key: "holderName",                label: "Name of Holder",                col: "(4)"  },
  { key: "holderEmail",               label: "Email of Holder",               col: "(5)"  },
  { key: "purposeOfInvestment",       label: "Purpose of Investment",         col: "(6)"  },
  { key: "investeeCompanyName",       label: "Investee Company Name",         col: "(7)"  },
  { key: "classOfSecurities",         label: "Class of Securities",           col: "(8)"  },
  { key: "numberOfSecurities",        label: "Number of Securities",          col: "(9)"  },
  { key: "clientIdOrDpNo",            label: "Client ID / DP No.",            col: "(10)" },
  { key: "faceValueOfSecurities",     label: "Face Value of Securities (₹)",  col: "(11)" },
  { key: "paidUpValueOfSecurities",   label: "Paid-Up Value of Securities (₹)", col: "(12)" },
  { key: "costOfAcquisition",         label: "Cost of Acquisition (₹)",       col: "(13)" },
  { key: "dateOfDisposal",            label: "Date of Disposal",              col: "(14)" },
  { key: "securitiesDisposed",        label: "Securities Disposed",           col: "(15)" },
  { key: "saleConsideration",         label: "Sale Consideration (₹)",        col: "(16)" },
  { key: "balanceSecurities",         label: "Balance Securities",            col: "(17)" },
  { key: "remarks",                   label: "Remarks",                       col: "(18)" },
];

// Part 1: cols (1)–(9), Part 2: S.No + cols (10)–(18)
const PART_ONE = ENTRY_COLUMNS.slice(0, 9);
const PART_TWO = [ENTRY_COLUMNS[0], ...ENTRY_COLUMNS.slice(9)];

const BLANK_ENTRY = {
  boardResolutionDate: "", investmentDate: "", holderName: "",
  holderEmail: "", purposeOfInvestment: "", investeeCompanyName: "",
  classOfSecurities: "", numberOfSecurities: "", clientIdOrDpNo: "",
  faceValueOfSecurities: "", paidUpValueOfSecurities: "", costOfAcquisition: "",
  dateOfDisposal: "", securitiesDisposed: "", saleConsideration: "",
  balanceSecurities: "", remarks: "",
};

const dateFields   = ["boardResolutionDate", "investmentDate", "dateOfDisposal"];
const numberFields = [
  "numberOfSecurities", "faceValueOfSecurities", "paidUpValueOfSecurities",
  "costOfAcquisition", "securitiesDisposed", "saleConsideration", "balanceSecurities",
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v) => (v === null || v === undefined || v === "") ? "—" : String(v);

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "—"; }
};

// ── Screen sub-table ──────────────────────────────────────────────────────────
const SubTable = ({ columns, rows, partLabel }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
    <div className="bg-slate-100 border-b border-slate-200 px-4 py-1.5">
      <span className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">{partLabel}</span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] border-collapse">
        <thead>
          <tr className="bg-slate-800 text-white">
            {columns.map(col => (
              <th key={col.key} className="border border-slate-600 px-2 py-1 text-center font-bold text-[9px] whitespace-nowrap">
                {col.col}
              </th>
            ))}
          </tr>
          <tr className="bg-slate-700 text-white">
            {columns.map(col => (
              <th
                key={col.key}
                className="border border-slate-600 px-2 py-2 text-center font-semibold text-[9px] leading-tight"
                style={{ minWidth: col.key === "serialNo" ? 36 : 100 }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
              {columns.map(col => (
                <td key={col.key} className="border border-slate-200 px-2 py-1.5 text-center text-[10px] align-middle">
                  {dateFields.includes(col.key) ? fmtDate(row[col.key]) : fmt(row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ── Add Entry Modal ───────────────────────────────────────────────────────────
const AddEntryModal = ({ onClose, onSave, saving, parentRecords }) => {
  const [selectedParentId, setSelectedParentId] = useState(
    parentRecords.length === 1 ? parentRecords[0].id : ""
  );
  const [form, setForm] = useState({ ...BLANK_ENTRY });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="bg-primary text-white px-6 py-3 rounded-t-xl flex justify-between items-center">
          <h2 className="font-bold text-sm uppercase tracking-widest">Add New MBP-3 Entry</h2>
          <button onClick={onClose} className="text-white hover:text-red-300 text-xl font-bold">✕</button>
        </div>

        <div className="overflow-y-auto p-4 flex-1">
          {parentRecords.length > 1 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-xs font-semibold text-blue-700 mb-1">Select Record *</label>
              <select
                value={selectedParentId}
                onChange={e => setSelectedParentId(e.target.value)}
                className="w-full px-2 py-1.5 border border-blue-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">— Select —</option>
                {parentRecords.map(p => (
                  <option key={p.id} value={p.id}>
                    ID: {p.id.slice(0, 8)}… | Signature: {p.signature || "—"}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {ENTRY_COLUMNS.filter(c => c.key !== "serialNo").map(col => (
              <div key={col.key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{col.col} {col.label}</label>
                <input
                  type={dateFields.includes(col.key) ? "date" : numberFields.includes(col.key) ? "number" : "text"}
                  value={form[col.key] || ""}
                  onChange={e => setForm(prev => ({ ...prev, [col.key]: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50 font-semibold uppercase">
            Cancel
          </button>
          <button
            onClick={() => onSave(form, selectedParentId)}
            disabled={saving || !selectedParentId}
            className="px-4 py-2 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 font-semibold uppercase"
          >
            {saving ? "Saving..." : "Save Entry"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Print styles ──────────────────────────────────────────────────────────────
const PRINT_STYLES = `
  @media print {
    @page { size: A3 landscape; margin: 8mm; }
    body * { visibility: hidden !important; }
    #mbp3-pdf-area, #mbp3-pdf-area * { visibility: visible !important; }
    #mbp3-pdf-area { position: fixed; top: 0; left: 0; width: 100%; font-family: Arial, sans-serif; }
  }
`;

// ── Main MBP-3 Viewer ─────────────────────────────────────────────────────────
const Mbp3Viewer = ({ selectedClientData }) => {
  const token     = useSelector((state) => state.auth?.access_token);
  const accountId = selectedClientData?.accountId || selectedClientData?.id;

  const [parentRecords, setParentRecords] = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [showModal,     setShowModal]     = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [status,        setStatus]        = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "mbp3-print-style";
    style.innerHTML = PRINT_STYLES;
    if (!document.getElementById("mbp3-print-style")) document.head.appendChild(style);
    return () => document.getElementById("mbp3-print-style")?.remove();
  }, []);

  const fetchData = useCallback(async () => {
    if (!accountId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await Mbp3RegisterApi.fetchMbp3Register(accountId, token);
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setParentRecords(list);
    } catch (err) {
      setError("No data available for MBP-3");
    } finally {
      setLoading(false);
    }
  }, [accountId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveEntry = async (form, parentId) => {
    setSaving(true);
    setStatus(null);
    try {
      const payload = {
        ...form,
        numberOfSecurities:       form.numberOfSecurities       ? parseInt(form.numberOfSecurities, 10)       : null,
        faceValueOfSecurities:    form.faceValueOfSecurities    ? parseFloat(form.faceValueOfSecurities)      : null,
        paidUpValueOfSecurities:  form.paidUpValueOfSecurities  ? parseFloat(form.paidUpValueOfSecurities)    : null,
        costOfAcquisition:        form.costOfAcquisition        ? parseFloat(form.costOfAcquisition)          : null,
        securitiesDisposed:       form.securitiesDisposed       ? parseInt(form.securitiesDisposed, 10)       : null,
        saleConsideration:        form.saleConsideration        ? parseFloat(form.saleConsideration)          : null,
        balanceSecurities:        form.balanceSecurities        ? parseInt(form.balanceSecurities, 10)        : null,
        boardResolutionDate:      form.boardResolutionDate      || null,
        investmentDate:           form.investmentDate           || null,
        dateOfDisposal:           form.dateOfDisposal           || null,
      };
      await Mbp3RegisterApi.addNewInvestments( parentId, payload, token);
      setShowModal(false);
      setStatus({ type: "success", message: "Entry added successfully!" });
      await fetchData();
    } catch (err) {
      setStatus({ type: "error", message: err.message || "Failed to add entry." });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = () => {
    setGeneratingPdf(true);
    setTimeout(() => { window.print(); setGeneratingPdf(false); }, 300);
  };

  const totalEntries = parentRecords.reduce((s, p) => s + (p.mbp3RegisterEntries?.length || 0), 0);

  if (!selectedClientData) {
    return <p className="text-center text-gray-500 mt-10">Select an account to view MBP-3 data.</p>;
  }

  // ── PDF sub-table ─────────────────────────────────────────────────────────
  const PdfSubTable = ({ columns, rows, partLabel }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: "bold", color: "#000", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.8 }}>
        {partLabel}
      </div>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#1e3a5f", color:  "#000" }}>
            {columns.map(col => (
              <th key={col.key} style={{ border: "1px solid #555", padding: "2px 3px", textAlign: "center", fontSize: 12, whiteSpace: "nowrap" }}>
                {col.col}
              </th>
            ))}
          </tr>
          <tr style={{ background: "#2d4f7c", color:  "#000" }}>
            {columns.map(col => (
              <th key={col.key} style={{ border: "1px solid #555", padding: "3px 3px", textAlign: "center", fontSize: 12, lineHeight: 1.3, minWidth: col.key === "serialNo" ? 24 : 72 }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ?  "#000" :  "#000" }}>
              {columns.map(col => (
                <td key={col.key} style={{ border: "1px solid  #000", padding: "2px 3px", textAlign: "center", fontSize: 11, verticalAlign: "middle" }}>
                  {dateFields.includes(col.key) ? fmtDate(row[col.key]) : fmt(row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="bg-white border-b-4 px-6 py-2 mb-2 text-center shadow-sm rounded-t-lg">
        <h1 className="text-2xl font-black uppercase tracking-tight">Form No. MBP - 3</h1>
        <h2 className="text-lg font-bold mt-1">Register of Investments</h2>
        <p className="text-[10px] italic text-slate-500 mt-1 max-w-4xl mx-auto">
          [Pursuant to section 187(1) of the Companies Act, 2013 and rule 14 of the
          Companies (Meetings of Board and its Powers) Rules 2014]
        </p>
      </div>

      {/* ── Controls ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mb-2 flex flex-wrap gap-3 items-end justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => { setShowModal(true); setStatus(null); }}
            disabled={!accountId}
            className="bg-primary text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            + Add New Entry
          </button>
          <button
            onClick={fetchData}
            className="bg-primary text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-blue-700 transition"
          >
            🔄 Reload
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={totalEntries === 0 || generatingPdf}
            className="bg-green-600 text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            ⬇ Download PDF
          </button>
        </div>
      </div>

      {/* ── Status Banner ── */}
      {status && (
        <div className={`mb-2 px-4 py-3 rounded-lg text-sm font-medium border ${
          status.type === "success"
            ? "bg-green-50 text-green-800 border-green-200"
            : "bg-red-50 text-red-800 border-red-200"
        }`}>
          {status.message}
        </div>
      )}

      {/* ── Screen: per-record groups ── */}
      {loading ? (
        <div className="text-center py-10 text-gray-500 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center py-10 text-red-500 text-sm">{error}</div>
      ) : totalEntries === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm italic">
          {accountId ? "No entries found for this company." : "Select a company to view records."}
        </div>
      ) : (
        <>
          {parentRecords.map((parent) => {
            const entries = parent.mbp3RegisterEntries || [];
            if (entries.length === 0) return null;

            return (
              <div key={parent.id} className="mb-8">

                {/* ── Record info bar — shown ONCE ── */}
                <div className="bg-slate-800 text-white px-4 py-2 rounded-t-xl flex flex-wrap gap-x-6 gap-y-1 text-xs">
                  <span><span className="text-slate-400 mr-1">Signature:</span><b>{fmt(parent.signature)}</b></span>
                  {/* <span><span className="text-slate-400 mr-1">Record ID:</span><b>{parent.id?.slice(0, 8)}…</b></span> */}
                </div>

                {/* ── Part 1: Columns (1)–(9) ── */}
                <div className="mt-3">
                  <SubTable columns={PART_ONE} rows={entries} partLabel="Part 1 — Columns (1) to (9)" />
                </div>

                {/* ── Part 2: S.No + Columns (10)–(18) ── */}
                <div className="mt-3">
                  <SubTable columns={PART_TWO} rows={entries} partLabel="Part 2 — Columns (10) to (18)" />
                </div>

                <div className="text-right text-xs text-gray-500 mt-1 pr-1">
                  Entries in this record: <span className="font-bold text-gray-700">{entries.length}</span>
                </div>
              </div>
            );
          })}

          <div className="text-right text-xs text-gray-500 mb-4 pr-1">
            Total entries: <span className="font-bold text-gray-700">{totalEntries}</span>
          </div>
        </>
      )}

      {/* ── Hidden PDF Print Area ── */}
      {totalEntries > 0 && (
        <div id="mbp3-pdf-area" style={{ display: "none" }}>
          <style>{`@media print { #mbp3-pdf-area { display: block !important; } }`}</style>

          {/* Global PDF title */}
          <div style={{ textAlign: "center", marginBottom: 10, fontFamily: "Arial, sans-serif" }}>
            <h1 style={{ fontSize: 15, fontWeight: "bold", margin: 0 }}>Form No. MBP - 3</h1>
            <h2 style={{ fontSize: 13, margin: "2px 0" }}>Register of Investments</h2>
            <p style={{ fontSize: 11, margin: 0, color: "#000", fontStyle: "italic" }}>
              [Pursuant to section 187(1) of the Companies Act, 2013 and rule 14 of the
              Companies (Meetings of Board and its Powers) Rules 2014]
            </p>
          </div>

          {parentRecords.map((parent, idx) => {
            const entries = parent.mbp3RegisterEntries || [];
            if (entries.length === 0) return null;

            return (
              <div key={parent.id} style={{ marginBottom: 20, fontFamily: "Arial, sans-serif" }}>

                {/* Record info — shown ONCE per record in PDF */}
                <div style={{
                  background: "#1e3a5f", color:  "#000",
                  padding: "5px 8px", marginBottom: 8,
                  fontSize: 12, borderRadius: 3,
                  display: "flex", flexWrap: "wrap", gap: "14px",
                }}>
                  <span><span style={{ color:  "#000" }}>Signature: </span><b>{fmt(parent.signature)}</b></span>
                  <span><span style={{ color:  "#000" }}>Record ID: </span><b>{parent.id?.slice(0, 8)}…</b></span>
                </div>

                {/* PDF Part 1 */}
                <PdfSubTable columns={PART_ONE} rows={entries} partLabel="Part 1 — Columns (1) to (9)" />

                {/* PDF Part 2 */}
                <PdfSubTable columns={PART_TWO} rows={entries} partLabel="Part 2 — Columns (10) to (18)" />

                {/* Divider between records */}
                {idx < parentRecords.length - 1 && (
                  <div style={{ borderTop: "1.5px dashed #94a3b8", marginTop: 14 }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add Entry Modal ── */}
      {showModal && (
        <AddEntryModal
          onClose={() => setShowModal(false)}
          onSave={handleSaveEntry}
          saving={saving}
          parentRecords={parentRecords}
        />
      )}
    </div>
  );
};

export default Mbp3Viewer;